import { type AssetDto, type AssetType, createAssetInput } from '@fos/shared/contracts/maintenance';
import { suggestMonitorUrl } from '@fos/shared/contracts/monitoring';
import type { BillingCycle } from '@fos/shared/contracts/services';
import { ASSET_TYPE_LABELS, BILLING_CYCLE_LABELS } from '@fos/shared/labels';
import { todayLocal } from '@fos/shared/time';
import { Plus } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field, Label } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Select, type SelectOption } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { issuesToErrors, parseAmountInput, zodIssues } from '../form-utils';
import { ASSET_TYPE_ICONS, defaultAddress, FORM_ASSET_TYPES, locationText } from '../maintenance-format';
import { ClientColorDot } from './MaintenanceBits';

const NONE = '__none__';
const OTHER_PLACE = '__other__';
const CYCLES: readonly BillingCycle[] = ['year', 'quarter', 'month', 'other'];
/** Pole adresu: skrzynka — email, serwer — nazwa albo IP (Przegląd Fali 10). */
const ADDRESS_LABELS: Partial<Record<AssetType, string>> = {
  mailbox: 'Adres skrzynki',
  server: 'Adres (nazwa albo IP)',
};
const ADDRESS_PLACEHOLDERS: Partial<Record<AssetType, string>> = {
  mailbox: 'np. kontakt@firma.pl',
  server: 'np. serwer.fravity.pl albo 203.0.113.10',
};
const SERVER_HINT = 'Porty i meldunek serwera dodasz w karcie zasobu (sekcja „Monitoring serwera”).';
const SERVER_NEXT_STEP = 'Dodaj meldunek albo port w sekcji „Monitoring serwera”.';

/** Typy z monitoringiem dostępności przy dodawaniu (Figma 08: „Monitoruj dostępność strony”). */
const MONITORED_TYPES: readonly AssetType[] = ['website', 'app'];

const FIELD_KEYS = [
  'type',
  'name',
  'clientId',
  'link',
  'provider',
  'server',
  'contract.price',
  'contract.nextPaymentDate',
  'contract',
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

interface FormState {
  type: AssetType;
  name: string;
  clientId: string | null;
  /** `project:<id>` / `service:<id>` / NONE */
  link: string;
  place: string;
  provider: string;
  server: string;
  price: string;
  cycle: BillingCycle;
  nextPaymentDate: string;
  remindOwner: boolean;
  remindClient: boolean;
  /** „Monitoruj dostępność strony” (strona, aplikacja) — monitor zakładany po zasobie */
  monitor: boolean;
}

function initial(clientId: string | null | undefined): FormState {
  return {
    type: 'website',
    name: '',
    clientId: clientId ?? null,
    link: NONE,
    place: '',
    provider: '',
    server: '',
    price: '',
    cycle: 'year',
    nextPaymentDate: '',
    remindOwner: true,
    remindClient: true,
    monitor: true,
  };
}

const placeValue = (p: { provider: string; server: string | null }) => `${p.provider}|${p.server ?? ''}`;

/**
 * Modal „Dodaj zasób” (Figma 08, 3045:4165): typ, adres, klient, projekt lub umowa, gdzie stoi, a dla nowej umowy
 * „Płatność od klienta” (kwota netto, cykl, następna płatność) z domyślnymi przypomnieniami. Kwota tylko z flagą
 * „Widzi stawki i kwoty”. „Monitoruj dostępność strony” (strona, aplikacja; domyślnie włączone) zakłada po zapisie
 * monitor zasobu (`monitoring.createMonitor`, tryb z integracji — przed konektorami „Symulacja”) z adresem
 * podpowiedzianym jak w karcie zasobu. Błąd monitora nie cofa zasobu: komunikat i „Włącz monitoring” w karcie zasobu.
 */
export function AddAssetDialog({
  open,
  onOpenChange,
  defaultClientId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** dodanie z profilu klienta */
  defaultClientId?: string | null;
  onSaved?: (asset: AssetDto) => void;
}) {
  const access = useAccess();
  const utils = trpc.useUtils();
  const seesRates = access.flag('seesRates');
  const options = trpc.maintenance.formOptions.useQuery(undefined, { enabled: open });
  const create = trpc.maintenance.createAsset.useMutation();
  const createMonitor = trpc.monitoring.createMonitor.useMutation();

  const [form, setForm] = useState<FormState>(() => initial(defaultClientId));
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setForm(initial(defaultClientId));
    setErrors({});
    setFormError(null);
  }, [open]);

  // najpierw miejsca z serwerem (np. „Zenbox s55” przed samym „Zenbox”)
  const locations = useMemo(
    () => [...(options.data?.locations ?? [])].sort((a, b) => Number(Boolean(b.server)) - Number(Boolean(a.server))),
    [options.data],
  );
  // domyślnie pierwsze znane miejsce (Figma: „Zenbox s55”)
  useEffect(() => {
    if (open && !form.place && locations[0]) setForm((f) => ({ ...f, place: placeValue(locations[0]!) }));
  }, [open, form.place, locations]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const client = options.data?.clients.find((c) => c.id === form.clientId);
  const clientOptions = useMemo<ComboboxOption[]>(
    () =>
      (options.data?.clients ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        leading: <ClientColorDot color={c.color} />,
      })),
    [options.data],
  );
  const linkOptions = useMemo<SelectOption[]>(() => {
    const list: SelectOption[] = [{ value: NONE, label: 'Bez projektu i umowy' }];
    for (const p of client?.projects ?? []) list.push({ value: `project:${p.id}`, label: `Projekt: ${p.name}` });
    for (const s of client?.services ?? []) list.push({ value: `service:${s.id}`, label: `Umowa: ${s.name}` });
    return list;
  }, [client]);
  const placeOptions = useMemo<SelectOption[]>(
    () => [
      ...locations.map((l) => ({ value: placeValue(l), label: locationText(l.provider, l.server) ?? l.provider })),
      { value: OTHER_PLACE, label: 'Inne miejsce…' },
    ],
    [locations],
  );
  const customPlace = locations.length === 0 || form.place === OTHER_PLACE;
  const linkedService = form.link.startsWith('service:')
    ? client?.services.find((s) => `service:${s.id}` === form.link)
    : undefined;
  const showPayment = seesRates && !linkedService;
  const canMonitor = MONITORED_TYPES.includes(form.type);
  const pending = create.isPending || createMonitor.isPending;

  /** Monitor nowego zasobu; wynik: 'on' albo komunikat (zasób zostaje, monitoring włączy się w karcie zasobu). */
  const startMonitoring = async (saved: AssetDto): Promise<'on' | string> => {
    const url = suggestMonitorUrl({ address: saved.address ?? null, name: saved.name });
    if (!url) return 'Monitoring włączysz w karcie zasobu — podaj tam adres strony.';
    try {
      await createMonitor.mutateAsync({ assetId: saved.id, url });
      return 'on';
    } catch (err) {
      const reason = errorMessage(err).replace(/\.?$/, '.');
      return `Nie udało się włączyć monitoringu: ${reason} Włączysz go w karcie zasobu.`;
    }
  };

  const monitorCheckbox = canMonitor ? (
    <Checkbox checked={form.monitor} onCheckedChange={(v) => set('monitor', v)} label="Monitoruj dostępność strony" />
  ) : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!form.clientId) {
      setErrors({ clientId: 'Wybierz klienta' });
      return;
    }
    let provider = form.provider.trim() || null;
    let server = form.server.trim() || null;
    if (!customPlace && form.place) {
      const [p = '', s = ''] = form.place.split('|');
      provider = p || null;
      server = s || null;
    }
    let contract: Record<string, unknown> | null = null;
    if (showPayment && form.price.trim()) {
      const price = parseAmountInput(form.price);
      if (!price) {
        setErrors({ 'contract.price': 'Podaj kwotę, np. 990 albo 1 990,50' });
        return;
      }
      contract = {
        price,
        cycle: form.cycle,
        nextPaymentDate: form.nextPaymentDate,
        periodStart: todayLocal(),
        remindOwner: form.remindOwner,
        remindClient: form.remindClient,
      };
    }
    const input = {
      type: form.type,
      name: form.name,
      address: defaultAddress(form.type, form.name),
      clientId: form.clientId,
      projectId: form.link.startsWith('project:') ? form.link.slice(8) : null,
      serviceId: form.link.startsWith('service:') ? form.link.slice(8) : null,
      provider,
      server,
      contract,
    };
    const parsed = createAssetInput.safeParse(input);
    if (!parsed.success) {
      setErrors(issuesToErrors(zodIssues(parsed.error), FIELD_KEYS));
      return;
    }
    try {
      const saved = await create.mutateAsync(input as Parameters<typeof create.mutateAsync>[0]);
      setErrors({});
      const monitorNote = canMonitor && form.monitor ? await startMonitoring(saved) : null;
      toast.success(`Dodano ${saved.name}${monitorNote === 'on' ? ' · monitoring włączony' : ''}`, {
        ...(saved.type === 'server' ? { description: SERVER_NEXT_STEP } : {}),
      });
      if (monitorNote && monitorNote !== 'on') toast.warning(monitorNote);
      await Promise.all([
        utils.maintenance.invalidate(),
        utils.services.invalidate(),
        ...(monitorNote === 'on' ? [utils.monitoring.invalidate()] : []),
      ]);
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      if (appErrorCode(err) === 'VALIDATION_ERROR') {
        const fieldErrors = issuesToErrors(validationIssues(err), FIELD_KEYS);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
      }
      setFormError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Dodaj zasób"
      className={FULLSCREEN_ON_PHONE}
      description="Strona, aplikacja, domena, skrzynka albo serwer, które utrzymujemy. Dostępy dodasz w karcie zasobu."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="add-asset-form" icon={Plus} loading={pending}>
            Dodaj zasób
          </Button>
        </>
      }
    >
      <form
        id="add-asset-form"
        ref={formRef}
        onSubmit={submit}
        noValidate
        className="flex flex-col gap-4"
        aria-label="Nowy zasób"
      >
        <div className="flex flex-col gap-1.5">
          <Label>Typ</Label>
          <div role="radiogroup" aria-label="Typ zasobu" className="flex flex-wrap gap-1.5">
            {FORM_ASSET_TYPES.map((t) => {
              const Icon = ASSET_TYPE_ICONS[t];
              const selected = form.type === t;
              return (
                // biome-ignore lint/a11y/useSemanticElements: przyciski w stylu Figmy zamiast natywnych radio
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => set('type', t)}
                  className={cn(
                    'inline-flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-body-s transition-colors',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                    selected
                      ? 'border-accent bg-accent-subtle font-medium text-fg'
                      : 'border-line text-fg-secondary hover:border-line-strong hover:text-fg',
                  )}
                >
                  <Icon size={15} strokeWidth={1.75} aria-hidden className={selected ? 'text-accent' : undefined} />
                  {ASSET_TYPE_LABELS[t]}
                </button>
              );
            })}
          </div>
        </div>

        <Field
          label={ADDRESS_LABELS[form.type] ?? 'Adres'}
          error={errors.name}
          hint={form.type === 'server' ? SERVER_HINT : undefined}
        >
          <Input
            name="name"
            autoFocus
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder={ADDRESS_PLACEHOLDERS[form.type] ?? 'np. novabud.pl'}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Klient" error={errors.clientId}>
            <Combobox
              value={form.clientId}
              onValueChange={(v) => setForm((f) => ({ ...f, clientId: v, link: NONE }))}
              options={clientOptions}
              placeholder={options.isPending ? 'Wczytywanie…' : 'Wybierz klienta'}
              searchPlaceholder="Szukaj klienta…"
              emptyText={options.isError ? 'Nie udało się wczytać klientów' : 'Brak klientów'}
            />
          </Field>
          <Field label="Projekt lub usługa" error={errors.link}>
            <Select value={form.link} onValueChange={(v) => set('link', v)} options={linkOptions} disabled={!client} />
          </Field>
        </div>

        {locations.length > 0 && (
          <Field label="Gdzie stoi">
            <Select value={form.place || undefined} onValueChange={(v) => set('place', v)} options={placeOptions} />
          </Field>
        )}
        {customPlace && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={locations.length === 0 ? 'Gdzie stoi: dostawca' : 'Dostawca'} error={errors.provider}>
              <Input value={form.provider} onChange={(e) => set('provider', e.target.value)} placeholder="np. Zenbox" />
            </Field>
            <Field label="Serwer" error={errors.server}>
              <Input value={form.server} onChange={(e) => set('server', e.target.value)} placeholder="np. s55" />
            </Field>
          </div>
        )}

        {linkedService && (
          <p className="text-caption text-fg-muted">Płatność i przypomnienia z umowy „{linkedService.name}”.</p>
        )}

        {showPayment && (
          <div className="flex flex-col gap-4 border-t border-line pt-4">
            <h3 className="text-body-s font-semibold text-fg">Płatność od klienta</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Kwota netto" error={errors['contract.price']} hint="Puste = bez umowy">
                <Input
                  mono
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                  placeholder="np. 990 zł"
                />
              </Field>
              <Field label="Cykl">
                <Select
                  value={form.cycle}
                  onValueChange={(v) => set('cycle', v as BillingCycle)}
                  options={CYCLES.map((c) => ({ value: c, label: BILLING_CYCLE_LABELS[c] }))}
                />
              </Field>
              <Field label="Następna płatność" error={errors['contract.nextPaymentDate'] ?? errors.contract}>
                <DateInput value={form.nextPaymentDate} onValueChange={(v) => set('nextPaymentDate', v)} />
              </Field>
            </div>
            <div className="flex flex-col gap-2.5">
              <Checkbox
                checked={form.remindOwner}
                onCheckedChange={(v) => set('remindOwner', v)}
                label="Przypomnij mi 30 i 7 dni przed"
              />
              <Checkbox
                checked={form.remindClient}
                onCheckedChange={(v) => set('remindClient', v)}
                label="Przypomnij klientowi 30 dni przed (mail przez Akceptacje)"
              />
              {monitorCheckbox}
            </div>
          </div>
        )}
        {!showPayment && monitorCheckbox}

        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
